import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Users, Clock, Beer, Wine, Sparkles, MapPin, ArrowRight, Calculator } from "lucide-react";

const CITIES = [
  { name: "Long Beach", miles: 0 },
  { name: "Huntington Beach", miles: 12 },
  { name: "Newport Beach", miles: 22 },
  { name: "Downtown LA", miles: 28 },
  { name: "Pasadena", miles: 35 },
  { name: "Malibu", miles: 55 },
  { name: "Riverside (IE)", miles: 50 },
  { name: "San Diego", miles: 105 },
  { name: "Ventura", miles: 80 },
  { name: "Other / further", miles: 60 },
];

const BEVERAGE_OPTIONS = [
  { key: "beer", label: "Beer", icon: Beer, addon: 0 },
  { key: "wine", label: "Wine", icon: Wine, addon: 100 },
  { key: "mixed", label: "Mixed", icon: Sparkles, addon: 125 },
  { key: "cocktails", label: "Cocktails", icon: Sparkles, addon: 200 },
];

function calcPrice({ guests, hours, taps, beverage, bartender, decoration, miles }) {
  // Floor case: 5 hrs / 1 tap / up to ~25 guests / beer / no extras = $500
  let base = 500;
  // Hours surcharge only above 5
  if (hours > 5) base += (hours - 5) * 75;
  // Tap surcharge: each additional tap above 1 = +$50
  base += (taps - 1) * 50;
  // Guest tiers (tiny events get the floor)
  if (guests > 250) base += 600;
  else if (guests > 175) base += 450;
  else if (guests > 125) base += 300;
  else if (guests > 75) base += 175;
  else if (guests > 25) base += 75;
  // Beverage add-on
  const bv = BEVERAGE_OPTIONS.find((o) => o.key === beverage);
  base += bv?.addon || 0;
  // Bartender
  if (bartender) base += 150;
  // Decoration package (we install — no DIY)
  if (decoration) base += 150;
  // Travel
  const billable = Math.max(0, miles - 25);
  const travel = billable * 2;
  const low = Math.round((base + travel) / 25) * 25;
  const high = Math.round((base * 1.15 + travel) / 25) * 25;
  return { low, high, travel, billable };
}

export default function QuoteCalculator() {
  const [guests, setGuests] = useState(25);
  const [hours, setHours] = useState(5);
  const [taps, setTaps] = useState(1);
  const [beverage, setBeverage] = useState("beer");
  const [bartender, setBartender] = useState(false);
  const [decoration, setDecoration] = useState(false);
  const [city, setCity] = useState(CITIES[0]);

  const quote = useMemo(
    () => calcPrice({ guests, hours, taps, beverage, bartender, decoration, miles: city.miles }),
    [guests, hours, taps, beverage, bartender, decoration, city]
  );

  const lockQuote = () => {
    const prefill = {
      event_location: city.name,
      guest_count: guests,
      taps_needed: taps,
      beverage_type: beverage,
      message: `Quote estimate: $${quote.low.toLocaleString()}–$${quote.high.toLocaleString()} (${hours}hr, ${taps} tap${taps>1?'s':''}, ${beverage}${bartender ? ", + bartender" : ""}${decoration ? ", + decoration pkg" : ""})`,
    };
    sessionStorage.setItem("trailhead_prefill", JSON.stringify(prefill));
    window.dispatchEvent(new Event("trailhead-prefill"));
    document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="calculator" className="bg-[#1f2e1f] text-[#e8dcc4] scroll-mt-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT — header + controls */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-12 bg-[#c97b3a]" />
              <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">003 — Build your ballpark</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl text-[#F8F5F0] leading-[0.9]">
              Build your<br />
              <span className="italic font-normal text-[#c97b3a]">ballpark.</span>
            </h2>
            <p className="text-[#e8dcc4]/70 mt-4 max-w-lg leading-relaxed">
              Slide things around. Watch the number move. Pin it to your inquiry when it feels right — final quote comes from us within 24 hours.
            </p>

            <div className="mt-10 space-y-9">
              {/* Guests */}
              <div data-testid="calc-guests">
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-[#e8dcc4]">
                    <Users className="h-4 w-4 text-[#c97b3a]" /> Guests
                  </Label>
                  <span className="font-display text-3xl text-[#F8F5F0]">{guests}</span>
                </div>
                <Slider value={[guests]} min={5} max={300} step={5} onValueChange={(v) => setGuests(v[0])} className="[&_[role=slider]]:bg-[#c97b3a] [&_[role=slider]]:border-[#c97b3a] [&>span:first-child]:bg-[#e8dcc4]/15 [&_.bg-primary]:bg-[#c97b3a]" />
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-[#e8dcc4]/50 mt-1.5"><span>5</span><span>300+</span></div>
              </div>

              {/* Hours */}
              <div data-testid="calc-hours">
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-[#e8dcc4]">
                    <Clock className="h-4 w-4 text-[#c97b3a]" /> Service Duration
                  </Label>
                  <span className="font-display text-3xl text-[#F8F5F0]">{hours} <span className="text-base text-[#e8dcc4]/60">hrs</span></span>
                </div>
                <Slider value={[hours]} min={3} max={8} step={1} onValueChange={(v) => setHours(v[0])} className="[&_[role=slider]]:bg-[#c97b3a] [&_[role=slider]]:border-[#c97b3a] [&>span:first-child]:bg-[#e8dcc4]/15 [&_.bg-primary]:bg-[#c97b3a]" />
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-[#e8dcc4]/50 mt-1.5"><span>3 hrs</span><span>8 hrs</span></div>
              </div>

              {/* Taps 1-4 */}
              <div data-testid="calc-taps">
                <Label className="text-sm font-semibold text-[#e8dcc4] mb-2 block">Taps flowing</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((n) => (
                    <button key={n} type="button" data-testid={`calc-taps-${n}`} onClick={() => setTaps(n)}
                      className={`px-3 py-3 border-2 transition-all font-medium ${taps === n ? "bg-[#c97b3a] border-[#c97b3a] text-white" : "bg-[#1f2e1f] border-[#e8dcc4]/25 text-[#e8dcc4] hover:border-[#c97b3a]"}`}>
                      <span className="font-display text-2xl">{n}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#e8dcc4]/55 mt-1.5">Each additional tap +$50. Maximum 4.</p>
              </div>

              {/* Beverage */}
              <div data-testid="calc-beverage">
                <Label className="text-sm font-semibold text-[#e8dcc4] mb-2 block">What's pouring</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {BEVERAGE_OPTIONS.map((b) => (
                    <button key={b.key} type="button" data-testid={`calc-beverage-${b.key}`} onClick={() => setBeverage(b.key)}
                      className={`px-3 py-3 border-2 transition-all text-sm font-medium flex flex-col items-center gap-1.5 ${beverage === b.key ? "bg-[#c97b3a] border-[#c97b3a] text-white" : "bg-[#1f2e1f] border-[#e8dcc4]/25 text-[#e8dcc4] hover:border-[#c97b3a]"}`}>
                      <b.icon className="h-4 w-4" />
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center justify-between bg-[#1f2e1f] border-2 border-[#e8dcc4]/15 px-4 py-3" data-testid="calc-bartender">
                  <div>
                    <Label htmlFor="bartender-sw" className="text-sm font-semibold text-[#e8dcc4] cursor-pointer">Bartender</Label>
                    <p className="text-xs text-[#e8dcc4]/60">+$150</p>
                  </div>
                  <Switch id="bartender-sw" checked={bartender} onCheckedChange={setBartender} className="data-[state=checked]:bg-[#c97b3a]" />
                </div>
                <div className="flex items-center justify-between bg-[#1f2e1f] border-2 border-[#e8dcc4]/15 px-4 py-3" data-testid="calc-decoration">
                  <div>
                    <Label htmlFor="deco-sw" className="text-sm font-semibold text-[#e8dcc4] cursor-pointer">Custom decoration</Label>
                    <p className="text-xs text-[#e8dcc4]/60">+$150 · we install (no DIY)</p>
                  </div>
                  <Switch id="deco-sw" checked={decoration} onCheckedChange={setDecoration} className="data-[state=checked]:bg-[#c97b3a]" />
                </div>
              </div>

              {/* Distance */}
              <div data-testid="calc-distance">
                <Label className="flex items-center gap-2 text-sm font-semibold text-[#e8dcc4] mb-2">
                  <MapPin className="h-4 w-4 text-[#c97b3a]" /> Where's the event
                </Label>
                <div className="flex flex-wrap gap-2">
                  {CITIES.map((c) => (
                    <button key={c.name} type="button" data-testid={`calc-city-${c.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      onClick={() => setCity(c)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-all ${city.name === c.name ? "bg-[#c97b3a] border-[#c97b3a] text-white" : "bg-[#1f2e1f] border-[#e8dcc4]/25 text-[#e8dcc4] hover:border-[#c97b3a]"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#e8dcc4]/60 mt-2">
                  {city.miles <= 25 ? "Free travel — within 25 mi of HQ." : `Travel fee for ${city.miles - 25} mi beyond 25 mi free zone: $${(city.miles - 25) * 2}`}
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — sticky price card (light on dark) */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="bg-[#f4ecd8] text-[#1f2e1f] p-8 md:p-10 relative overflow-hidden border-2 border-[#c97b3a]/40">
              <div className="absolute -top-1 -right-1 bg-[#c97b3a] text-white px-3 py-1 rotate-3 text-[10px] uppercase tracking-[0.3em] font-bold">Estimate</div>

              <div className="flex items-center gap-2 mb-4">
                <Calculator className="h-4 w-4 text-[#c97b3a]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#c97b3a] font-semibold">Your ballpark</span>
              </div>

              <div className="font-display leading-[0.85] tracking-tight" data-testid="calc-price-output">
                <div className="text-xs uppercase tracking-[0.3em] text-[#5a5245] mb-2">From</div>
                <div className="text-7xl md:text-8xl text-[#1f2e1f]">${quote.low.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#5a5245] mt-3 mb-1">Up to</div>
                <div className="text-4xl md:text-5xl italic font-normal text-[#c97b3a]">${quote.high.toLocaleString()}</div>
              </div>

              <div className="mt-7 pt-7 border-t border-[#1f2e1f]/15 space-y-2 text-sm">
                <Row label="Guests" value={`${guests}`} />
                <Row label="Duration" value={`${hours} hours`} />
                <Row label="Taps" value={`${taps}`} />
                <Row label="Pouring" value={BEVERAGE_OPTIONS.find((b) => b.key === beverage)?.label} />
                {bartender && <Row label="Bartender" value="+$150" accent />}
                {decoration && <Row label="Decoration pkg" value="+$150" accent />}
                <Row label="Travel" value={quote.billable > 0 ? `$${quote.travel}` : "free"} accent={quote.billable > 0} />
              </div>

              <Button onClick={lockQuote} data-testid="calc-lock-quote"
                className="btn-orange w-full mt-7 rounded-none py-6 text-base font-semibold">
                Lock this quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <div className="mt-3 px-3 py-2 bg-[#1f2e1f]/8 border border-[#1f2e1f]/15 text-[11px] text-[#1f2e1f] text-center leading-relaxed">
                <strong className="font-semibold">Alcohol not included.</strong> You bring the kegs — see the <a href="#byo" className="underline decoration-[#c97b3a] underline-offset-2 hover:text-[#c97b3a]">BYO guide</a> below.
              </div>
              <p className="text-[11px] text-[#5a5245] text-center mt-2 italic">Ballpark only — final quote comes from us within 24 hours.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#5a5245] text-xs uppercase tracking-wider">{label}</span>
    <span className={`font-medium ${accent ? "text-[#c97b3a]" : "text-[#1f2e1f]"}`}>{value}</span>
  </div>
);
